import { ProjectLeadAssignment } from '../models/project';

// item 164 — Project Lead payout splits. The approval wizard won't let a proposal
// through unless the leads are valid; extracted from the component so the gate logic
// (and its tolerance) is unit-tested rather than buried in render.

export const leadPercentTotal = (leads: ProjectLeadAssignment[]): number => (
  leads.reduce((s, l) => s + (Number(l.percent) || 0), 0)
);

// "balanced" = no leads assigned, OR the percentages total 100 (within float tolerance)
export const leadsBalanced = (leads: ProjectLeadAssignment[]): boolean => (
  leads.length === 0 || Math.abs(leadPercentTotal(leads) - 100) < 0.01
);

// every assigned lead has a chosen user
export const leadsComplete = (leads: ProjectLeadAssignment[]): boolean => leads.every((l) => !!l.userId);

// item 555 — the backend (ProjectLead.ValidateAssignments) ALSO forbids the same user twice
// and any lead at 0%; mirror those rules here so the wizard blocks + explains before submit
// instead of failing on a generic server error.
// no user is chosen more than once (only counts rows that have a user picked)
export const leadsUnique = (leads: ProjectLeadAssignment[]): boolean => {
  const chosen = leads.map((l) => l.userId).filter(Boolean);
  return new Set(chosen).size === chosen.length;
};

// every assigned lead has a percentage greater than 0
export const leadsAllPositive = (leads: ProjectLeadAssignment[]): boolean => (
  leads.every((l) => (Number(l.percent) || 0) > 0)
);

export interface LeadValidation {
  valid: boolean;
  total: number;
  complete: boolean;
  balanced: boolean;
  unique: boolean;
  allPositive: boolean;
}

// the combined approval gate: an empty list is fine; otherwise every lead needs a distinct
// user, each percentage must be > 0, and the percentages must total 100.
export const validateLeads = (leads: ProjectLeadAssignment[]): LeadValidation => {
  const total = leadPercentTotal(leads);
  const complete = leadsComplete(leads);
  const balanced = leadsBalanced(leads);
  const unique = leadsUnique(leads);
  const allPositive = leadsAllPositive(leads);
  return {
    valid: leads.length === 0 || (complete && balanced && unique && allPositive),
    total,
    complete,
    balanced,
    unique,
    allPositive,
  };
};
